import type { SupabaseClient } from "@supabase/supabase-js";

export interface Profile {
  id: string;
  username: string | null;
  is_public: boolean;
  donations_total: number;
  avatar_url: string | null;
  profile_views: number;
  is_premium: boolean;
  is_lifetime_premium: boolean;
  subscription_product_id: string | null;
  subscription_end: string | null;
  discord_webhook: string | null;
  created_at?: string;
}

/**
 * Fetches a user's profile from the database
 */
export async function getProfile(
  supabase: SupabaseClient,
  userId: string
): Promise<Profile> {
  console.log('[getProfile] Querying profile for userId:', userId);

  const { data, error } = await supabase
    .from("profiles")
    .select("*")  // Select all columns to see what's actually available
    .eq("id", userId)
    .single();

  if (error) {
    console.error('[getProfile] Database error:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    throw error;
  }

  if (!data) {
    console.error('[getProfile] No data returned for userId:', userId);
    throw new Error('Profile not found');
  }

  // Log the actual data structure returned
  console.log('[getProfile] Raw data from database (all fields):', {
    userId,
    dataKeys: Object.keys(data),
    hasId: 'id' in data,
    id: data.id,
    hasUsername: 'username' in data,
    username: data.username,
    usernameType: typeof data.username,
    sample: { id: data.id, username: data.username, is_public: data.is_public },
  });

  // Return only the fields we need for the Profile interface
  return {
    id: data.id,
    username: data.username,
    is_public: data.is_public,
    donations_total: data.donations_total,
    avatar_url: data.avatar_url,
    profile_views: data.profile_views,
    is_premium: data.is_premium,
    is_lifetime_premium: data.is_lifetime_premium,
    subscription_product_id: data.subscription_product_id,
    subscription_end: data.subscription_end,
    discord_webhook: data.discord_webhook,
    created_at: data.created_at,
  };
}

/**
 * Updates a user's profile with read-back guarantee
 */
export async function updateProfile(
  supabase: SupabaseClient,
  userId: string,
  updates: Partial<Omit<Profile, "id">>
): Promise<Profile> {
  // Perform the update on profiles_internal (the actual table)
  // The profiles view is not updatable due to CASE statements
  const { error: updateError } = await supabase
    .from("profiles_internal")
    .update(updates)
    .eq("id", userId);

  if (updateError) throw updateError;

  // Read back to ensure the update was persisted
  const fresh = await getProfile(supabase, userId);

  // Verify critical fields were actually updated
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined && fresh[key as keyof Profile] !== value) {
      throw new Error(`Failed to persist ${key} - expected ${value} but got ${fresh[key as keyof Profile]}`);
    }
  }

  return fresh;
}
